// src/types/chat.ts
export enum UserRole {
  GUEST = 'guest',
  OPERATOR = 'operator',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
}

export enum ChatType {
    ONE_ON_ONE = 'one_on_one',
    ROOM_CHAT = 'room_chat',
    GROUP_CHAT = 'group_chat',
  }
  
  export enum MessageType {
    TEXT = 'text',
    IMAGE = 'image',
    SYSTEM = 'system',
    LLM_RESPONSE = 'llm_response',
    ACTIVITY_RESERVATION = 'activity_reservation',
    SERVICE_TASK = 'service_task',
  }
  
  export interface Reservation {
    id: string;
    confirmationNumber: string;
    reservationId?: string; // ID from GSC API
    profileId?: string; // ID from GSC API
    checkInDate: string;
    checkOutDate: string;
    status: string;
    guestId: string;
    roomId: string;
  }

  export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    hotelId?: string;
    roomId?: string; // DEPRECATED for operators: Use rooms[] instead. Still used for guests.
    rooms?: { id: string; roomNumber: string }[]; // For operators: all assigned rooms
    reservationId?: string;
    profileId?: string; // ID from GSC API
    reservation?: Reservation;
    // Guest-specific fields
    phoneNumber?: string;
    birthdate?: string; // YYYY-MM-DD format
    nationalId?: string;
    nationality?: string;
    sex?: string; // M, F, or Other
    country?: string;
    city?: string;
    postCode?: string;
    passportId?: string;
    profilePicture?: string;
  }

  export interface Room {
    id: string;
    roomNumber: string;
    roomType?: string;
    floor?: string;
    status: string;
    guests?: User[];
  }
  
  export interface Message {
    id: string;
    content: string;
    type: MessageType;
    imageUrl?: string;
    isFromLLM: boolean;
    llmModel?: string;
    senderId: string;
    chatId: string;
    createdAt: string;
    sender?: User;
    replyToId?: string;
    replyTo?: Message;
    metadata?: ActivityReservationMetadata | ServiceTaskMetadata | GuestCommentMetadata;
  }

  export interface ActivityReservationMetadata {
    activityId?: string | number; // Can be string from GSC API ResultID or number from legacy data
    guestName: string;
    activityName: string;
    date: string;
    session: string;
    notes?: string;
    status: 'pending' | 'confirmed' | 'cancelled';
  }

  export interface ServiceTaskMetadata {
    taskId?: string | number; // Can be string from GSC API ResultID or number from legacy data
    localTaskId?: string; // UUID from local database (used for confirmation before getting numeric PMS ID)
    requestedByUserId?: string;
    guestProfileId?: string;
    guestName: string;
    taskType: string;
    localityType: string;
    locality: string;
    priority: 'High' | 'Normal' | 'Low';
    notes: string;
    dueDate?: string;
    status: 'pending' | 'completed' | 'cancelled';
    imageUrl?: string;
  }

  export interface GuestCommentMetadata {
    guestName: string;
    commentText: string;
    status: 'Positive' | 'Neutral' | 'Negative';
    /** Numeric type id when PMS uses numeric IDs (legacy / display). */
    commentTypeId?: number;
    /** PMS CommentTypeID string (preferred for API round-trip). */
    commentTypeID?: string;
    /** PMS guest comment row id — required for replies/updates. */
    commentId?: number;
    /** When true, comment exists but PMS CommentID has not been resolved yet. */
    pendingCommentId?: boolean;
    profileId?: string;
    reservationId?: string;
    replyNote?: string;
  }
  
  export interface Chat {
    id: string;
    name: string;
    type: ChatType;
    roomId?: string;
    reservationId?: string;
    isLLMPresent: boolean;
    aiEnabled: boolean;
    isAdminOnly: boolean;
    isStaffOnly: boolean;
    operatorId?: string;
    createdById: string;
    createdAt: string;
    updatedAt: string;
    createdBy?: User;
    operator?: User;
    room?: Room;
    reservation?: Reservation;
    messages?: Message[];
  }

  export interface Hotel {
    id: string;
    name: string;
    code: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    phone?: string;
    email?: string;
    website?: string;
    description?: string;
    activities?: (string | { name: string; details?: string })[];
    rules?: string[];
    policies?: Record<string, any>;
    amenities?: string[];
    settings?: {
      timezone?: string;
      currency?: string;
      language?: string;
      checkInTime?: string;
      checkOutTime?: string;
      aiEnabled?: boolean;
      maxGuests?: number;
      starRating?: number;
      nightModeEnabled?: boolean;
      nightModeStartTime?: string;
      nightModeEndTime?: string;
      allowEditGuestNameFromPms?: boolean;
      guestMustInitiateContact?: boolean;
      gdprTexts?: Record<string, string>;
      gdprTextVersion?: string;
    };
    whatsappLineId?: string;
    dashboardCompanyId?: string;
    dashboardBranchId?: string;
    dashboardChatbotName?: string;
    aiAgentProvider?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }

  export interface GuestStatistics {
    totalGuests: number;
    activeGuests: number;
    inactiveGuests: number;
    guestsByHotel: {
      hotelId: string;
      hotelName: string;
      totalGuests: number;
      activeGuests: number;
      inactiveGuests: number;
    }[];
  }